import { useEffect, useMemo, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  cancelAdminContribution,
  listAdminContributions,
  listPartnersAdmin,
  type ContribKind,
  type ContribState,
  type Contribution,
  type Partner,
} from '../lib/admin';

const KINDS: ContribKind[] = ['litres', 'article_stock'];
const STATES: ContribState[] = ['pending', 'committed', 'cancelled'];

export function AdminPartnerContributions() {
  const { localePath, locale } = useI18n();
  const { accessToken } = useAuth();
  const [list, setList] = useState<Contribution[] | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnerFilter, setPartnerFilter] = useState<'' | number>('');
  const [kindFilter, setKindFilter] = useState<ContribKind | ''>('');
  const [stateFilter, setStateFilter] = useState<ContribState | ''>('');
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    if (!accessToken) return;
    setErr(null);
    try {
      const items = await listAdminContributions(accessToken, {
        partnerId: partnerFilter === '' ? undefined : partnerFilter,
        kind: kindFilter || undefined,
        state: stateFilter || undefined,
      });
      setList(items);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    }
  }

  useEffect(() => {
    (async () => {
      if (!accessToken) return;
      try {
        const list = await listPartnersAdmin(accessToken);
        setPartners(list);
      } catch {}
    })();
  }, [accessToken]);

  useEffect(() => { load(); }, [accessToken, partnerFilter, kindFilter, stateFilter]);

  async function onCancel(id: number) {
    if (!confirm('Cancel this contribution? Bypasses the 24h grace window.')) return;
    try {
      await cancelAdminContribution(accessToken!, id);
      await load();
    } catch (e: any) {
      alert(e?.message || 'Cancel failed');
    }
  }

  const partnerMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of partners) m.set(p.id, p.name);
    return m;
  }, [partners]);

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Partner contributions</h1>
          </header>
          <p class="admin-lede">
            Every contribution logged by partners across the network. Partners get a 24h grace window to cancel;
            admins can override and cancel committed entries from here.
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <label class="admin-field" style={{ minWidth: 200 }}>
              <span>Partner</span>
              <select
                value={partnerFilter === '' ? '' : String(partnerFilter)}
                onChange={(e) => {
                  const v = (e.target as HTMLSelectElement).value;
                  setPartnerFilter(v === '' ? '' : Number(v));
                }}
              >
                <option value="">All partners</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label class="admin-field" style={{ minWidth: 160 }}>
              <span>Kind</span>
              <select
                value={kindFilter}
                onChange={(e) => setKindFilter((e.target as HTMLSelectElement).value as ContribKind | '')}
              >
                <option value="">All kinds</option>
                {KINDS.map((k) => <option key={k} value={k}>{k === 'litres' ? 'Litres' : 'Article stock'}</option>)}
              </select>
            </label>
            <label class="admin-field" style={{ minWidth: 160 }}>
              <span>State</span>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter((e.target as HTMLSelectElement).value as ContribState | '')}
              >
                <option value="">All states</option>
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>

          {err && <p class="admin-error">{err}</p>}
          {!list && !err && <p class="admin-lede">Loading…</p>}

          {list && list.length === 0 && (
            <p class="admin-lede">No contributions match these filters.</p>
          )}

          {list && list.length > 0 && (
            <table class="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Partner</th>
                  <th>Kind</th>
                  <th>Entry</th>
                  <th>State</th>
                  <th>Created by</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((c) => {
                  const when = formatWhen(c.createdAt, locale);
                  const partnerName = partnerMap.get(c.partnerId) ?? `#${c.partnerId}`;
                  const entry = c.kind === 'litres'
                    ? `+${c.litres ?? 0} L`
                    : `+${c.stockCount ?? 0} × ${c.articleTitle ?? `#${c.articleId}`}`;
                  return (
                    <tr key={c.id} style={{ opacity: c.state === 'cancelled' ? 0.5 : 1 }}>
                      <td class="admin-mono">{when}</td>
                      <td>{partnerName}</td>
                      <td>{c.kind === 'litres' ? 'Litres' : 'Article stock'}</td>
                      <td>
                        {entry}
                        {c.note && (
                          <div class="admin-mono" style={{ fontSize: 10, color: 'rgba(245,237,224,0.5)' }}>
                            {c.note}
                          </div>
                        )}
                      </td>
                      <td>{c.state}</td>
                      <td class="admin-mono" style={{ fontSize: 11 }}>{c.createdByUserId.slice(0, 8)}…</td>
                      <td>
                        {c.state !== 'cancelled' && (
                          <button class="admin-link" style={{ color: '#ff8b6b' }} onClick={() => onCancel(c.id)}>
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </RequireAdmin>
  );
}

function formatWhen(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
